import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ApiService from '../../api/ApiService';
import { useAuth } from '../../components/auth/AuthContext';
import CanvasWorkspaceSidebar from '../../components/CanvasWorkspaceSidebar/CanvasWorkspaceSidebar';
import { MODEL_CONFIGS } from '../../components/canvas/nodes/modelConfigs';
import { GPT_IMAGE_2_CONFIG, NANO_BANANA_2_CONFIG } from '../../components/canvas/nodes/imageModelConfigs';
import {
  MINIMAX_H3_CONFIG,
  KLING_O3_REFERENCE_CONFIG,
  GEMINI_OMNI_FLASH_CONFIG,
  SUPER_SEED_2_LITE_CONFIG,
} from '../../components/canvas/nodes/videoModelConfigs';
import { useTaskQuote } from '../../components/canvas/nodes/useTaskQuote';
import {
  formatTaskPoints,
  formatTaskTime,
  getTaskModelName,
  getTaskPreview,
  getTaskPrompt,
  getTaskStatusMeta,
} from '../../utils/taskRecord';
import './GeneratePage.css';

const apiService = new ApiService();

const IMAGE_NODE_CONFIGS = {
  gptImage2: GPT_IMAGE_2_CONFIG,
  nanoBanana2: NANO_BANANA_2_CONFIG,
};

const VIDEO_NODE_CONFIGS = {
  minimaxH3Video: MINIMAX_H3_CONFIG,
  klingO3ReferenceVideo: KLING_O3_REFERENCE_CONFIG,
  geminiOmniFlashVideo: GEMINI_OMNI_FLASH_CONFIG,
  superSeed2LiteVideo: SUPER_SEED_2_LITE_CONFIG,
};

const IMAGE_MODELS = MODEL_CONFIGS.image.map(model => ({
  ...IMAGE_NODE_CONFIGS[model.type],
  ...model,
  kind: 'image',
  title: model.name,
  displayIcon: model.icon,
}));

const VIDEO_MODELS = MODEL_CONFIGS.video.map(model => ({
  ...VIDEO_NODE_CONFIGS[model.type],
  ...model,
  kind: 'video',
  title: model.name,
  displayIcon: model.icon,
}));

const MODEL_GROUPS = {
  image: IMAGE_MODELS,
  video: VIDEO_MODELS,
};

const MODEL_MODE_OPTIONS = [
  { value: 'image', label: '图片模型', icon: 'bi-image' },
  { value: 'video', label: '视频模型', icon: 'bi-camera-reels' },
];

const RESOLUTION_OPTIONS = ['1K', '2K', '4K'];
const QUALITY_OPTIONS = [
  { value: 'low', label: '快速', detail: '响应更快' },
  { value: 'medium', label: '标准', detail: '平衡质量与速度' },
  { value: 'high', label: '精细', detail: '细节优先' },
];

const TERMINAL_STATUSES = new Set(['completed', 'failed']);
const HISTORY_PAGE_SIZE = 12;
const REFERENCE_TYPE_ORDER = ['image', 'video', 'audio'];
const REFERENCE_TYPE_OPTIONS = {
  image: {
    label: '参考图',
    icon: 'bi-image',
    accept: 'image/png,image/jpeg,image/webp,image/gif',
    extensions: 'PNG、JPG、WEBP',
  },
  video: {
    label: '参考视频',
    icon: 'bi-camera-reels',
    accept: 'video/mp4,video/quicktime,video/webm,video/x-matroska',
    extensions: 'MP4、MOV、WEBM',
  },
  audio: {
    label: '参考音频',
    icon: 'bi-music-note-beamed',
    accept: 'audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/aac,audio/flac',
    extensions: 'MP3、WAV、M4A',
  },
};

const REFERENCE_FILE_EXTENSIONS = {
  image: /\.(png|jpe?g|webp|gif)$/i,
  video: /\.(mp4|mov|webm|mkv)$/i,
  audio: /\.(mp3|wav|ogg|m4a|aac|flac)$/i,
};

const getStatusMeta = status => ({
  pending: { label: '排队中', className: 'is-pending' },
  processing: { label: '生成中', className: 'is-processing' },
  completed: { label: '已完成', className: 'is-completed' },
  failed: { label: '失败', className: 'is-failed' },
}[status] || { label: '等待提交', className: 'is-idle' });

const toMenuOptions = values => (values || []).map(option => (
  typeof option === 'string' ? { value: option, label: option } : option
));

const getMenuOptionLabel = (options, value) => (
  options.find(option => String(option.value) === String(value))?.label || String(value)
);

const isReferenceFile = (file, type) => (
  Boolean(file)
  && (file.type?.startsWith(`${type}/`) || REFERENCE_FILE_EXTENSIONS[type].test(file.name || ''))
);

const createReferencePreviewUrl = file => (
  typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(file)
    : ''
);

const revokeReferencePreviewUrl = url => {
  if (url?.startsWith('blob:') && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
};

const parseMaybeJson = value => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
};

const collectMediaResults = value => {
  const results = [];
  const visited = new WeakSet();

  const addUrl = (url, metadata = {}, hint = '') => {
    if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) return;
    if (/\.(mp3|wav|ogg|aac|m4a)(\?|$)/i.test(url)) return;
    if (results.some(result => result.url === url)) return;
    const type = /video|movie|clip/i.test(hint) || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)
      ? 'video'
      : 'image';
    results.push({
      url,
      type,
      width: metadata.width,
      height: metadata.height,
      duration: metadata.duration,
    });
  };

  const visit = (current, hint = '') => {
    const parsed = parseMaybeJson(current);
    if (!parsed) return;
    if (typeof parsed === 'string') {
      addUrl(parsed, {}, hint);
      return;
    }
    if (typeof parsed !== 'object' || visited.has(parsed)) return;
    visited.add(parsed);

    if (Array.isArray(parsed)) {
      parsed.forEach(item => visit(item, hint));
    } else {
      addUrl(parsed.url || parsed.image_url || parsed.imageUrl || parsed.video_url || parsed.videoUrl, parsed, hint);
      Object.entries(parsed).forEach(([key, child]) => visit(child, key));
    }
  };

  visit(value);
  return results;
};

function GeneratePageOptionMenu({
  id,
  label,
  value,
  options,
  disabled,
  isOpen,
  onToggle,
  onSelect,
}) {
  const selectedOption = options.find(option => String(option.value) === String(value)) || options[0];
  const menuId = `${id}-options`;

  return (
    <div className="GeneratePage-option-field">
      <span>{label}</span>
      <div className={`GeneratePage-option-menu${isOpen ? ' is-open' : ''}`}>
        <button
          type="button"
          id={`${id}-trigger`}
          className="GeneratePage-option-trigger"
          aria-label={`${label}：${selectedOption?.label || value}`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={menuId}
          disabled={disabled}
          onClick={() => onToggle(id)}
          onKeyDown={event => {
            if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onToggle(id);
            }
          }}
        >
          <span>{selectedOption?.label || value}</span>
          <i className="bi bi-chevron-down" aria-hidden="true" />
        </button>
        {isOpen && (
          <div id={menuId} className="GeneratePage-option-list" role="listbox" aria-label={`${label}选项`}>
            {options.map(option => {
              const isSelected = String(option.value) === String(value);
              return (
                <button
                  type="button"
                  role="option"
                  key={option.value}
                  aria-selected={isSelected}
                  className={isSelected ? 'is-selected' : ''}
                  onClick={() => onSelect(option.value)}
                  onKeyDown={event => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      onToggle(null);
                    }
                  }}
                >
                  <span>{option.label}</span>
                  {option.detail && <small>{option.detail}</small>}
                  {isSelected && <i className="bi bi-check2" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function GeneratePageModelPicker({ model, models, isOpen, disabled, onToggle, onSelect }) {
  return (
    <div className="GeneratePage-model-picker">
      <button
        type="button"
        className={`GeneratePage-model-picker-trigger${isOpen ? ' is-open' : ''}`}
        aria-label={`选择生成模型：${model.name}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        style={{ '--model-accent': model.color || model.accent }}
        onClick={onToggle}
      >
        <span className="GeneratePage-model-mark" aria-hidden="true">
          <i className={`bi ${model.displayIcon}`} />
        </span>
        <span className="GeneratePage-model-picker-copy">
          <strong>{model.name}</strong>
          <small>{model.description}</small>
        </span>
        <span className="GeneratePage-model-picker-action">
          <span>切换</span>
          <i className="bi bi-chevron-down" aria-hidden="true" />
        </span>
      </button>
      {isOpen && (
        <div className="GeneratePage-model-menu" role="listbox" aria-label="模型选项">
          <div className="GeneratePage-model-menu-header">
            <span>选择模型</span>
            <small>{models.length} 个可用</small>
          </div>
          <div className="GeneratePage-model-menu-list">
            {models.map(option => {
              const isSelected = option.type === model.type;
              return (
                <button
                  type="button"
                  role="option"
                  key={option.type}
                  className={`GeneratePage-model-option${isSelected ? ' is-selected' : ''}`}
                  aria-label={option.name}
                  aria-selected={isSelected}
                  style={{ '--model-accent': option.color || option.accent }}
                  onClick={() => onSelect(option.type)}
                >
                  <span className="GeneratePage-model-mark" aria-hidden="true">
                    <i className={`bi ${option.displayIcon}`} />
                  </span>
                  <span className="GeneratePage-model-copy">
                    <strong>{option.name}</strong>
                    <small>{option.description}</small>
                    <span className="GeneratePage-model-tags">
                      {option.tags.map(tag => <em key={tag}>{tag}</em>)}
                    </span>
                  </span>
                  <i className="bi bi-check2 GeneratePage-model-check" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function GeneratePageReferenceSection({
  model,
  assets,
  hiddenAssetCount,
  counts,
  limits,
  limitIssues,
  uploading,
  disabled,
  error,
  inputRefs,
  getAvailableSlots,
  onOpenPicker,
  onFiles,
  onRemove,
}) {
  const enabledTypes = REFERENCE_TYPE_ORDER.filter(type => limits[type] > 0);
  if (enabledTypes.length === 0) return null;

  const referenceSummary = enabledTypes.map(type => REFERENCE_TYPE_OPTIONS[type].label).join('、');

  return (
    <div className="GeneratePage-section GeneratePage-reference-section">
      <div className="GeneratePage-section-heading">
        <div>
          <h2>参考素材</h2>
          <p>{model.referenceHint || `${referenceSummary}按当前模型上限提交`}</p>
        </div>
        <span className="GeneratePage-section-count">{assets.length} 项</span>
      </div>

      <div className="GeneratePage-reference-actions" aria-label="添加参考素材">
        {enabledTypes.map(type => {
          const option = REFERENCE_TYPE_OPTIONS[type];
          const availableSlots = getAvailableSlots(type);
          return (
            <div className="GeneratePage-reference-action" key={type}>
              <button
                type="button"
                className="GeneratePage-reference-add"
                disabled={disabled || uploading || availableSlots === 0}
                onClick={() => onOpenPicker(type)}
                aria-label={`添加${option.label}`}
              >
                <i className={`bi ${option.icon}`} aria-hidden="true" />
                <span>{option.label}</span>
                <small>{counts[type]}/{limits[type]}</small>
              </button>
              <input
                ref={element => {
                  inputRefs.current[type] = element;
                }}
                className="GeneratePage-reference-input"
                type="file"
                accept={option.accept}
                multiple
                aria-label={`上传${option.label}`}
                onChange={event => onFiles(type, event)}
              />
            </div>
          );
        })}
      </div>

      {hiddenAssetCount > 0 && (
        <div className="GeneratePage-reference-notice">
          <i className="bi bi-info-circle" aria-hidden="true" />
          <span>当前模型暂不支持的 {hiddenAssetCount} 项素材不会提交。</span>
        </div>
      )}

      {limitIssues.length > 0 && (
        <div className="GeneratePage-reference-error" role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{limitIssues[0]}</span>
        </div>
      )}

      {error && (
        <div className="GeneratePage-reference-error" role="alert">
          <i className="bi bi-cloud-exclamation" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {assets.length > 0 && (
        <div className="GeneratePage-reference-list" aria-label="已添加参考素材">
          {assets.map(asset => {
            const option = REFERENCE_TYPE_OPTIONS[asset.type];
            const source = asset.previewUrl || asset.url;
            return (
              <div className={`GeneratePage-reference-item is-${asset.type}`} key={asset.id}>
                <div className="GeneratePage-reference-preview">
                  {asset.type === 'image' && source && <img src={source} alt={asset.name} />}
                  {asset.type === 'video' && source && <video src={source} playsInline preload="metadata" aria-label={asset.name} />}
                  {asset.type === 'audio' && <i className={`bi ${option.icon}`} aria-hidden="true" />}
                  {asset.status === 'uploading' && <span className="GeneratePage-reference-uploading">上传中</span>}
                  {asset.status === 'ready' && <span className="GeneratePage-reference-ready"><i className="bi bi-check2" /></span>}
                </div>
                <div className="GeneratePage-reference-copy">
                  <strong title={asset.name}>{asset.name}</strong>
                  <small>{asset.status === 'uploading' ? '正在上传到素材库' : option.label}</small>
                </div>
                <button
                  type="button"
                  className="GeneratePage-reference-remove"
                  aria-label={`删除${asset.name}`}
                  disabled={disabled}
                  onClick={() => onRemove(asset.id)}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const getErrorMessage = (response, fallback = '生成失败，请检查参数后重试') => {
  const detail = response?.data?.error || response?.data?.message || response?.message || response?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

function GeneratePage() {
  const history = useHistory();
  const { user, openLogin } = useAuth();
  const modelMenuRef = useRef(null);
  const parameterMenuRef = useRef(null);
  const referenceInputRefs = useRef({});
  const referenceAssetsRef = useRef([]);
  const [modelMode, setModelMode] = useState('image');
  const [selectedModelType, setSelectedModelType] = useState('gptImage2');
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState(GPT_IMAGE_2_CONFIG.defaultAspectRatio);
  const [resolution, setResolution] = useState('1K');
  const [quality, setQuality] = useState(GPT_IMAGE_2_CONFIG.defaultQuality);
  const [duration, setDuration] = useState(MINIMAX_H3_CONFIG.defaultDuration);
  const [generateAudio, setGenerateAudio] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [mediaResults, setMediaResults] = useState([]);
  const [taskError, setTaskError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const [openParameterMenu, setOpenParameterMenu] = useState(null);
  const [isParamsMenuOpen, setIsParamsMenuOpen] = useState(false);
  const historyRequestVersion = useRef(0);
  const [historyTasks, setHistoryTasks] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isHistoryLoadingMore, setIsHistoryLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [referenceAssets, setReferenceAssets] = useState([]);
  const [referenceUploadsInFlight, setReferenceUploadsInFlight] = useState(0);
  const [referenceError, setReferenceError] = useState('');

  const modelOptions = MODEL_GROUPS[modelMode];

  const selectedModel = useMemo(
    () => modelOptions.find(model => model.type === selectedModelType) || modelOptions[0],
    [modelOptions, selectedModelType]
  );
  const taskIsActive = Boolean(activeTask && !TERMINAL_STATUSES.has(activeTask.status));
  const controlsDisabled = isSubmitting || taskIsActive;
  const isVideoModel = selectedModel.kind === 'video';
  const referenceLimits = useMemo(() => ({
    image: Number(selectedModel.imageLimit) || 0,
    video: Number(selectedModel.videoLimit) || 0,
    audio: Number(selectedModel.audioLimit) || 0,
  }), [selectedModel]);
  const visibleReferenceAssets = useMemo(
    () => referenceAssets.filter(asset => referenceLimits[asset.type] > 0),
    [referenceAssets, referenceLimits]
  );
  const hiddenReferenceAssetCount = referenceAssets.length - visibleReferenceAssets.length;
  const referenceCounts = useMemo(() => visibleReferenceAssets.reduce((counts, asset) => ({
    ...counts,
    [asset.type]: counts[asset.type] + 1,
  }), { image: 0, video: 0, audio: 0 }), [visibleReferenceAssets]);
  const readyReferenceGroups = useMemo(() => REFERENCE_TYPE_ORDER.reduce((groups, type) => ({
    ...groups,
    [type]: visibleReferenceAssets
      .filter(asset => asset.type === type && asset.status === 'ready' && asset.url)
      .map(asset => asset.url),
  }), { image: [], video: [], audio: [] }), [visibleReferenceAssets]);
  const referenceLimitIssues = useMemo(() => {
    const issues = REFERENCE_TYPE_ORDER
      .filter(type => referenceCounts[type] > referenceLimits[type])
      .map(type => `${REFERENCE_TYPE_OPTIONS[type].label}不能超过 ${referenceLimits[type]} 个`);
    const totalReferenceCount = referenceCounts.image + referenceCounts.video;
    if (selectedModel.referenceLimit && totalReferenceCount > selectedModel.referenceLimit) {
      issues.push(`参考图和参考视频合计不能超过 ${selectedModel.referenceLimit} 个`);
    }
    return issues;
  }, [referenceCounts, referenceLimits, selectedModel.referenceLimit]);
  const submitDisabled = controlsDisabled
    || !prompt.trim()
    || referenceUploadsInFlight > 0
    || referenceLimitIssues.length > 0;
  const aspectRatioOptions = useMemo(() => toMenuOptions(selectedModel.aspectRatios), [selectedModel]);
  const resolutionOptions = useMemo(() => toMenuOptions(
    isVideoModel ? selectedModel.resolutions : RESOLUTION_OPTIONS
  ), [isVideoModel, selectedModel]);
  const durationOptions = useMemo(() => (
    isVideoModel
      ? selectedModel.durationOptions.map(value => ({ value, label: `${value} 秒` }))
      : []
  ), [isVideoModel, selectedModel]);
  const parameterSummary = useMemo(() => {
    const summary = [
      getMenuOptionLabel(aspectRatioOptions, aspectRatio),
      getMenuOptionLabel(resolutionOptions, resolution),
    ];
    if (isVideoModel) {
      summary.push(`${duration} 秒`);
      if (selectedModel.supportsAudio) summary.push(generateAudio ? '有声' : '无声');
    } else {
      summary.push(getMenuOptionLabel(QUALITY_OPTIONS, quality));
    }
    return summary.join(' · ');
  }, [aspectRatio, aspectRatioOptions, duration, generateAudio, isVideoModel, quality, resolution, resolutionOptions, selectedModel.supportsAudio]);

  const inputParams = useMemo(() => {
    if (isVideoModel) {
      return selectedModel.buildParams(
        prompt.trim(),
        readyReferenceGroups.image,
        { duration, aspectRatio, resolution, generateAudio },
        readyReferenceGroups.video,
        readyReferenceGroups.audio,
      );
    }

    return {
      prompt: prompt.trim(),
      [selectedModel.ratioParam]: aspectRatio,
      resolution,
      output_format: selectedModel.defaultOutputFormat || 'png',
      ...(readyReferenceGroups.image.length ? { image_urls: readyReferenceGroups.image } : {}),
      ...(selectedModel.supportsQuality ? { quality, num_images: 1 } : {}),
      ...(selectedModel.supportsNumImages ? { num_images: selectedModel.fixedNumImages || 1 } : {}),
    };
  }, [aspectRatio, duration, generateAudio, isVideoModel, prompt, quality, readyReferenceGroups, resolution, selectedModel]);

  const taskQuote = useTaskQuote({
    enabled: Boolean(user?.token && prompt.trim() && !taskIsActive),
    apiVersion: 'v3',
    model: selectedModel.modelId,
    params: inputParams,
  });

  const loadHistory = useCallback(async (nextPage = 1, append = false) => {
    if (!user?.token || typeof apiService.get_fal_task_list !== 'function') return;
    const version = ++historyRequestVersion.current;
    if (append) setIsHistoryLoadingMore(true);
    else setIsHistoryLoading(true);
    setHistoryError('');

    try {
      const response = await apiService.get_fal_task_list(
        user.token,
        nextPage,
        HISTORY_PAGE_SIZE,
        '',
        '',
        { silent: true },
      );
      if (version !== historyRequestVersion.current) return;
      if (response?.code !== 200) throw new Error('历史记录加载失败');

      const nextTasks = Array.isArray(response?.data?.list) ? response.data.list : [];
      setHistoryTasks(current => (append ? [...current, ...nextTasks] : nextTasks));
      setHistoryTotal(Number(response?.data?.total) || 0);
      setHistoryPage(nextPage);
    } catch (_) {
      if (version === historyRequestVersion.current) setHistoryError('历史记录加载失败，请稍后重试');
    } finally {
      if (version === historyRequestVersion.current) {
        setIsHistoryLoading(false);
        setIsHistoryLoadingMore(false);
      }
    }
  }, [user?.token]);

  useEffect(() => {
    historyRequestVersion.current += 1;
    setHistoryTasks([]);
    setHistoryTotal(0);
    setHistoryPage(1);
    setHistoryError('');
    loadHistory(1, false);
  }, [loadHistory]);

  useEffect(() => {
    referenceAssetsRef.current = referenceAssets;
  }, [referenceAssets]);

  useEffect(() => () => {
    referenceAssetsRef.current.forEach(asset => revokeReferencePreviewUrl(asset.previewUrl));
  }, []);

  const getAvailableReferenceSlots = useCallback((type) => {
    const typeLimit = referenceLimits[type];
    if (!typeLimit) return 0;

    let available = typeLimit - referenceCounts[type];
    if ((type === 'image' || type === 'video') && selectedModel.referenceLimit) {
      available = Math.min(
        available,
        selectedModel.referenceLimit - referenceCounts.image - referenceCounts.video,
      );
    }
    return Math.max(0, available);
  }, [referenceCounts, referenceLimits, selectedModel.referenceLimit]);

  const openReferencePicker = useCallback((type) => {
    referenceInputRefs.current[type]?.click();
  }, []);

  const handleReferenceFiles = useCallback(async (type, event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const option = REFERENCE_TYPE_OPTIONS[type];
    const availableSlots = getAvailableReferenceSlots(type);
    if (availableSlots === 0) {
      setReferenceError(`${option.label}已达到当前模型上限`);
      return;
    }

    const validFiles = files.filter(file => isReferenceFile(file, type));
    const filesToUpload = validFiles.slice(0, availableSlots);
    const notices = [];
    if (validFiles.length !== files.length) {
      notices.push(`${option.label}仅支持 ${option.extensions}`);
    }
    if (validFiles.length > availableSlots) {
      notices.push(`当前模型本次最多还能添加 ${availableSlots} 个${option.label}`);
    }
    setReferenceError(notices.join('；'));
    if (!filesToUpload.length) return;

    const uploadItems = filesToUpload.map((file, index) => ({
      id: `reference-${type}-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      file,
      name: file.name,
      previewUrl: createReferencePreviewUrl(file),
      url: '',
      status: 'uploading',
    }));
    setReferenceAssets(current => [...current, ...uploadItems]);
    setReferenceUploadsInFlight(current => current + uploadItems.length);

    await Promise.all(uploadItems.map(async item => {
      try {
        const result = await apiService.uploadImageToR2(item.file);
        if (!result?.public_url) throw new Error(`${option.label}上传后没有返回可用地址`);
        setReferenceAssets(current => current.map(asset => asset.id === item.id
          ? { ...asset, url: result.public_url, status: 'ready' }
          : asset));
      } catch (error) {
        revokeReferencePreviewUrl(item.previewUrl);
        setReferenceAssets(current => current.filter(asset => asset.id !== item.id));
        setReferenceError(error?.message || `${option.label}上传失败，请重试`);
      } finally {
        setReferenceUploadsInFlight(current => Math.max(0, current - 1));
      }
    }));
  }, [getAvailableReferenceSlots]);

  const removeReference = useCallback((assetId) => {
    setReferenceAssets(current => {
      const asset = current.find(item => item.id === assetId);
      revokeReferencePreviewUrl(asset?.previewUrl);
      return current.filter(item => item.id !== assetId);
    });
  }, []);

  useEffect(() => {
    if (!aspectRatioOptions.some(option => option.value === aspectRatio)) {
      setAspectRatio(selectedModel.defaultAspectRatio);
    }
  }, [aspectRatio, aspectRatioOptions, selectedModel.defaultAspectRatio]);

  useEffect(() => {
    if (!resolutionOptions.some(option => option.value === resolution)) {
      setResolution(selectedModel.defaultResolution || '1K');
    }
  }, [resolution, resolutionOptions, selectedModel.defaultResolution]);

  useEffect(() => {
    const closeMenusOnPointerDown = event => {
      if (modelMenuRef.current && !modelMenuRef.current.contains(event.target)) {
        setIsModelMenuOpen(false);
      }
      if (parameterMenuRef.current && !parameterMenuRef.current.contains(event.target)) {
        setOpenParameterMenu(null);
        setIsParamsMenuOpen(false);
      }
    };
    const closeMenusOnEscape = event => {
      if (event.key !== 'Escape') return;
      setIsModelMenuOpen(false);
      setOpenParameterMenu(null);
      setIsParamsMenuOpen(false);
    };

    document.addEventListener('mousedown', closeMenusOnPointerDown);
    document.addEventListener('keydown', closeMenusOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeMenusOnPointerDown);
      document.removeEventListener('keydown', closeMenusOnEscape);
    };
  }, []);

  const applyModelDefaults = nextModel => {
    setAspectRatio(nextModel.defaultAspectRatio);
    setResolution(nextModel.defaultResolution || '1K');
    setQuality(nextModel.defaultQuality || 'medium');
    setDuration(nextModel.defaultDuration || MINIMAX_H3_CONFIG.defaultDuration);
    setGenerateAudio(Boolean(nextModel.defaultGenerateAudio));
  };

  const selectModel = modelType => {
    if (controlsDisabled) return;
    const nextModel = modelOptions.find(model => model.type === modelType);
    if (!nextModel) return;
    setSelectedModelType(modelType);
    applyModelDefaults(nextModel);
    setTaskError('');
    setIsModelMenuOpen(false);
    setOpenParameterMenu(null);
    setIsParamsMenuOpen(false);
  };

  const selectModelMode = nextMode => {
    if (controlsDisabled || nextMode === modelMode) return;
    const nextModel = MODEL_GROUPS[nextMode][0];
    setModelMode(nextMode);
    setSelectedModelType(nextModel.type);
    applyModelDefaults(nextModel);
    setTaskError('');
    setIsModelMenuOpen(false);
    setOpenParameterMenu(null);
    setIsParamsMenuOpen(false);
  };

  const toggleParameterMenu = menuId => {
    setIsModelMenuOpen(false);
    setOpenParameterMenu(current => current === menuId ? null : menuId);
  };

  const pollTask = useCallback(async taskId => {
    const response = await apiService.queryV3Task(taskId);
    const data = response?.data || {};
    const status = data.status === 'completed'
      ? 'completed'
      : data.status === 'failed' || data.status === 'error'
        ? 'failed'
        : 'processing';
    const output = data.output || data.result || data.output_params || null;
    const nextMediaResults = status === 'completed' ? collectMediaResults(output) : [];

    setActiveTask(current => current?.taskId === taskId
      ? { ...current, status, outputParams: output }
      : current);
    if (status === 'completed') {
      setMediaResults(nextMediaResults);
      setTaskError('');
      loadHistory(1, false);
    }
    if (status === 'failed') {
      setTaskError(getErrorMessage(data, '生成失败，请检查参数后重试'));
    }
    return status;
  }, [loadHistory]);

  useEffect(() => {
    const taskId = activeTask?.taskId;
    if (!taskId || TERMINAL_STATUSES.has(activeTask.status)) return undefined;

    let cancelled = false;
    let timerId = null;
    const poll = async () => {
      try {
        const status = await pollTask(taskId);
        if (!cancelled && !TERMINAL_STATUSES.has(status)) {
          timerId = setTimeout(poll, 3000);
        }
      } catch (_) {
        if (!cancelled) {
          setTaskError('暂时无法读取任务状态，请稍后重试');
          setActiveTask(current => current?.taskId === taskId
            ? { ...current, status: 'failed' }
            : current);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [activeTask?.taskId, pollTask]);

  const handleGenerate = async () => {
    if (isSubmitting || taskIsActive) return;
    if (!prompt.trim()) {
      setTaskError('先写下你想生成的画面，再开始任务');
      return;
    }
    if (!user?.token) {
      openLogin('/generate');
      return;
    }
    if (referenceUploadsInFlight > 0) {
      setTaskError('参考素材仍在上传，请稍候');
      return;
    }
    if (referenceLimitIssues.length > 0) {
      setTaskError(referenceLimitIssues[0]);
      return;
    }

    setIsSubmitting(true);
    setTaskError('');
    setMediaResults([]);
    try {
      const confirmedQuote = taskQuote.status === 'success'
        ? taskQuote
        : await taskQuote.quoteNow();
      const response = await apiService.createV3Task(selectedModel.modelId, inputParams);
      const taskId = response?.data?.task_id;
      if (!taskId) throw new Error(getErrorMessage(response, `创建${isVideoModel ? '视频' : '图片'}任务失败`));

      setActiveTask({
        taskId,
        status: 'pending',
        modelName: selectedModel.name,
        modelId: selectedModel.modelId,
        kind: selectedModel.kind,
        params: inputParams,
        chargedPoints: Number(response?.data?.price ?? confirmedQuote.points),
      });
      toast.success(`${isVideoModel ? '视频' : '图片'}任务已创建`);
    } catch (error) {
      setTaskError(error?.message || getErrorMessage(error, '生成失败，请重试'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetResult = () => {
    setActiveTask(null);
    setMediaResults([]);
    setTaskError('');
  };

  const statusMeta = getStatusMeta(activeTask?.status);
  const resultKind = activeTask?.kind || selectedModel.kind;
  const historyHasMore = historyTasks.length < historyTotal;
  const promptPlaceholder = isVideoModel
    ? '例如：黄昏海边的手持镜头，浪花拍打礁石，镜头缓慢向前推进，电影感…'
    : selectedModel.type === 'gptImage2'
      ? '例如：雨夜的上海街头，一位穿红色风衣的女孩撑伞走过霓虹灯下，电影感构图…'
      : '例如：一只戴圆框眼镜的橘猫坐在老式书店窗边，午后阳光，细节丰富…';

  return (
    <div className="GeneratePage-root">
      <CanvasWorkspaceSidebar
        activeKey="generate"
        onInspiration={() => history.push('/')}
        onGenerate={() => {}}
        onAssets={() => history.push('/task-records')}
        onCanvas={() => history.push({ pathname: '/', search: '?tab=mine' })}
      />

      <main className="GeneratePage-main">
        <header className="GeneratePage-header">
          <div>
            <h1>生成工作台</h1>
          </div>
        </header>

        <div className="GeneratePage-workspace">
          <section className="GeneratePage-controls" aria-label="生成设置">
            <div className="GeneratePage-section GeneratePage-model-section">
              <div className="GeneratePage-section-heading">
                <div>
                  <h2>选择模型</h2>
                  <p>切换类型后选择适合当前任务的模型</p>
                </div>
                <span className="GeneratePage-section-count">{modelOptions.length} 个</span>
              </div>
              <div className="GeneratePage-model-tabs" role="tablist" aria-label="模型类型">
                {MODEL_MODE_OPTIONS.map(option => (
                  <button
                    type="button"
                    key={option.value}
                    role="tab"
                    className={`GeneratePage-model-tab${modelMode === option.value ? ' is-selected' : ''}`}
                    aria-selected={modelMode === option.value}
                    aria-controls="GeneratePage-model-picker"
                    disabled={controlsDisabled}
                    onClick={() => selectModelMode(option.value)}
                  >
                    <i className={`bi ${option.icon}`} aria-hidden="true" />
                    <span>{option.label.replace('模型', '')}</span>
                    <small>{MODEL_GROUPS[option.value].length}</small>
                  </button>
                ))}
              </div>
              <div ref={modelMenuRef} id="GeneratePage-model-picker">
                <GeneratePageModelPicker
                  model={selectedModel}
                  models={modelOptions}
                  isOpen={isModelMenuOpen}
                  disabled={controlsDisabled}
                  onToggle={() => {
                    setOpenParameterMenu(null);
                    setIsModelMenuOpen(current => !current);
                  }}
                  onSelect={selectModel}
                />
              </div>
            </div>

            <div className="GeneratePage-section GeneratePage-prompt-section">
              <div className="GeneratePage-section-heading">
                <div>
                  <h2>描述画面</h2>
                  <p>描述主体、动作、环境和画面气质</p>
                </div>
                <span className="GeneratePage-required">必填</span>
              </div>
              <label className="GeneratePage-prompt-field">
                <span className="sr-only">画面提示词</span>
                <textarea
                  value={prompt}
                  rows="4"
                  maxLength={2000}
                  aria-label="画面提示词"
                  placeholder={promptPlaceholder}
                  disabled={controlsDisabled}
                  aria-describedby="GeneratePage-prompt-count"
                  onChange={event => {
                    setPrompt(event.target.value);
                    if (taskError) setTaskError('');
                  }}
                />
                <span id="GeneratePage-prompt-count" className="GeneratePage-prompt-count">
                  {prompt.length} / 2000
                </span>
              </label>
            </div>

            <GeneratePageReferenceSection
              model={selectedModel}
              assets={visibleReferenceAssets}
              hiddenAssetCount={hiddenReferenceAssetCount}
              counts={referenceCounts}
              limits={referenceLimits}
              limitIssues={referenceLimitIssues}
              uploading={referenceUploadsInFlight > 0}
              disabled={controlsDisabled}
              error={referenceError}
              inputRefs={referenceInputRefs}
              getAvailableSlots={getAvailableReferenceSlots}
              onOpenPicker={openReferencePicker}
              onFiles={handleReferenceFiles}
              onRemove={removeReference}
            />

            <div
              ref={parameterMenuRef}
              className={`GeneratePage-section GeneratePage-params-section${isParamsMenuOpen ? ' is-open' : ''}`}
            >
              <button
                type="button"
                className="GeneratePage-params-toggle"
                aria-expanded={isParamsMenuOpen}
                aria-controls="GeneratePage-params-panel"
                disabled={controlsDisabled}
                onClick={() => {
                  setIsModelMenuOpen(false);
                  setOpenParameterMenu(null);
                  setIsParamsMenuOpen(current => !current);
                }}
              >
                <span className="GeneratePage-params-toggle-mark" aria-hidden="true">
                  <i className="bi bi-sliders2" />
                </span>
                <span className="GeneratePage-params-toggle-copy">
                  <strong>生成参数</strong>
                  <small>{parameterSummary}</small>
                </span>
                <span className="GeneratePage-params-toggle-action">
                  <em>{isParamsMenuOpen ? '收起' : '调整'}</em>
                  <i className="bi bi-chevron-down" aria-hidden="true" />
                </span>
              </button>

              {isParamsMenuOpen && (
                <div id="GeneratePage-params-panel" className="GeneratePage-params-panel">
                  <div className="GeneratePage-field-grid">
                    <GeneratePageOptionMenu
                      id="GeneratePage-aspect-ratio"
                      label="画面比例"
                      value={aspectRatio}
                      options={aspectRatioOptions}
                      disabled={controlsDisabled}
                      isOpen={openParameterMenu === 'GeneratePage-aspect-ratio'}
                      onToggle={toggleParameterMenu}
                      onSelect={value => {
                        setAspectRatio(value);
                        setOpenParameterMenu(null);
                      }}
                    />
                    <GeneratePageOptionMenu
                      id="GeneratePage-resolution"
                      label="分辨率"
                      value={resolution}
                      options={resolutionOptions}
                      disabled={controlsDisabled}
                      isOpen={openParameterMenu === 'GeneratePage-resolution'}
                      onToggle={toggleParameterMenu}
                      onSelect={value => {
                        setResolution(value);
                        setOpenParameterMenu(null);
                      }}
                    />
                    {isVideoModel && (
                      <GeneratePageOptionMenu
                        id="GeneratePage-duration"
                        label="视频时长"
                        value={duration}
                        options={durationOptions}
                        disabled={controlsDisabled}
                        isOpen={openParameterMenu === 'GeneratePage-duration'}
                        onToggle={toggleParameterMenu}
                        onSelect={value => {
                          setDuration(Number(value));
                          setOpenParameterMenu(null);
                        }}
                      />
                    )}
                  </div>

                  {isVideoModel && selectedModel.supportsAudio && (
                    <label className="GeneratePage-audio-toggle">
                      <input
                        type="checkbox"
                        checked={generateAudio}
                        disabled={controlsDisabled}
                        onChange={event => setGenerateAudio(event.target.checked)}
                      />
                      <span className="GeneratePage-toggle-track" aria-hidden="true"><i /></span>
                      <span>生成原生音频</span>
                    </label>
                  )}

                  {selectedModel.supportsQuality && (
                    <fieldset className="GeneratePage-choice-field">
                      <legend>生成质量</legend>
                      <div className="GeneratePage-quality-options">
                        {QUALITY_OPTIONS.map(option => (
                          <button
                            type="button"
                            key={option.value}
                            className={quality === option.value ? 'is-selected' : ''}
                            aria-pressed={quality === option.value}
                            disabled={controlsDisabled}
                            onClick={() => setQuality(option.value)}
                          >
                            <strong>{option.label}</strong>
                            <small>{option.detail}</small>
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  )}

                  <div className="GeneratePage-parameter-note">
                    <i className="bi bi-info-circle" aria-hidden="true" />
                    <span>{isVideoModel
                      ? '当前为文生视频模式，可在画布中继续补充参考素材。'
                      : selectedModel.type === 'nanoBanana2'
                        ? 'Nano 2 当前每次生成 1 张图片。'
                        : 'GPT 2 默认输出 PNG，并支持高精度文字呈现。'}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="GeneratePage-submit-area">
              <div className={`GeneratePage-quote is-${taskQuote.status}`} aria-live="polite">
                <span className="GeneratePage-quote-icon" aria-hidden="true"><i className="bi bi-lightning-charge-fill" /></span>
                <span>
                  {taskQuote.status === 'success' && <><strong>预计 {taskQuote.points} 积分</strong><small>提交时以最终报价为准</small></>}
                  {taskQuote.status === 'loading' && <><strong>正在计算积分</strong><small>稍等片刻</small></>}
                  {taskQuote.status === 'error' && <><strong>报价暂不可用</strong><small>提交时将再次尝试</small></>}
                  {taskQuote.status === 'idle' && <><strong>输入提示词后计算积分</strong><small>生成前会再次确认</small></>}
                </span>
              </div>
              <button
                type="button"
                className="GeneratePage-submit"
                disabled={submitDisabled}
                onClick={handleGenerate}
              >
                {isSubmitting ? <><i className="bi bi-arrow-repeat GeneratePage-spin" />正在创建任务</> : <><i className={`bi ${isVideoModel ? 'bi-camera-reels' : 'bi-stars'}`} />{user?.token ? `生成${isVideoModel ? '视频' : '图片'}` : '登录后生成'}</>}
              </button>
            </div>
          </section>

          <section className="GeneratePage-results" aria-label="生成结果" aria-live="polite">
            <div className="GeneratePage-results-header">
              <div>
                <h2>生成结果</h2>
                <p className={activeTask ? 'GeneratePage-result-meta' : undefined}>
                  {activeTask ? (
                    <>
                      <span>{activeTask.modelName}</span>
                      <span className="GeneratePage-task-id">任务 ID：{activeTask.taskId}</span>
                    </>
                  ) : '结果会显示在这里'}
                </p>
              </div>
              <div className="GeneratePage-result-actions">
                {activeTask && <span className={`GeneratePage-status ${statusMeta.className}`}><i />{statusMeta.label}</span>}
                {activeTask && TERMINAL_STATUSES.has(activeTask.status) && (
                  <button type="button" className="GeneratePage-clear-button" onClick={handleResetResult} aria-label="清空生成结果">
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>

            <div className="GeneratePage-current-result">
              {!activeTask && !taskError && (
                <div className="GeneratePage-empty-state">
                  <span className="GeneratePage-empty-mark" aria-hidden="true"><i className={`bi ${isVideoModel ? 'bi-camera-reels' : 'bi-image'}`} /></span>
                  <h3>{isVideoModel ? '把下一段镜头交给 AI' : '把下一帧画面交给 AI'}</h3>
                  <p>左侧选择模型并写下提示词，生成后的{isVideoModel ? '视频' : '图片'}会在这里展开。</p>
                </div>
              )}

              {taskIsActive && (
                <div className={`GeneratePage-processing-state${resultKind === 'video' ? ' is-video' : ''}`}>
                  <div className="GeneratePage-processing-copy">
                    <span className="GeneratePage-processing-mark"><i className={`bi ${resultKind === 'video' ? 'bi-camera-reels' : 'bi-stars'}`} /></span>
                    <div>
                      <h3>{activeTask.status === 'pending' ? '任务已进入队列' : `正在生成你的${resultKind === 'video' ? '片段' : '画面'}`}</h3>
                      <p>通常需要一点时间，页面会自动更新结果。</p>
                    </div>
                  </div>
                  <div className="GeneratePage-skeleton-grid" aria-hidden="true">
                    <span />
                  </div>
                </div>
              )}

              {activeTask?.status === 'completed' && mediaResults.length === 0 && !taskError && (
                <div className={`GeneratePage-empty-state is-completed${resultKind === 'video' ? ' is-video' : ''}`}>
                  <span className="GeneratePage-empty-mark" aria-hidden="true"><i className="bi bi-check2-circle" /></span>
                  <h3>任务已完成</h3>
                  <p>结果正在同步，请稍后刷新任务记录查看。</p>
                </div>
              )}

              {mediaResults.length > 0 && (
                <div className="GeneratePage-media-grid">
                  {mediaResults.map((media, index) => (
                    <figure className={`GeneratePage-media-result is-${media.type}`} key={media.url}>
                      {media.type === 'video' ? (
                        <video
                          src={media.url}
                          controls
                          playsInline
                          preload="metadata"
                          aria-label={`${activeTask?.modelName || '模型'}生成视频 ${index + 1}`}
                        />
                      ) : (
                        <img src={media.url} alt={`${activeTask?.modelName || '模型'}生成结果 ${index + 1}`} />
                      )}
                      <figcaption>
                        <span>{media.width && media.height ? `${media.width} × ${media.height}` : media.duration ? `${media.duration} 秒` : `结果 ${String(index + 1).padStart(2, '0')}`}</span>
                        <a href={media.url} target="_blank" rel="noreferrer" aria-label={`打开并下载结果 ${index + 1}`}>
                          <i className="bi bi-download" />下载
                        </a>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}

              {taskError && (
                <div className="GeneratePage-error-state" role="alert">
                  <span className="GeneratePage-error-mark" aria-hidden="true"><i className="bi bi-exclamation-triangle" /></span>
                  <div>
                    <h3>{taskError}</h3>
                    <p>可以保留当前参数，修改提示词后再次尝试。</p>
                  </div>
                  <button type="button" onClick={handleResetResult}>重新配置</button>
                </div>
              )}
            </div>

            <section className="GeneratePage-history" aria-label="历史记录">
              <div className="GeneratePage-history-header">
                <div>
                  <h3>历史记录</h3>
                  <p>{user?.token
                    ? historyTotal > 0 ? `共 ${historyTotal.toLocaleString('zh-CN')} 条任务` : '最近生成会出现在这里'
                    : '登录后同步你的生成任务'}</p>
                </div>
                {user?.token && historyTasks.length > 0 && (
                  <span className="GeneratePage-history-count">{historyTasks.length}/{historyTotal || historyTasks.length}</span>
                )}
              </div>

              {!user?.token && (
                <div className="GeneratePage-history-state">
                  <i className="bi bi-clock-history" aria-hidden="true" />
                  <span>登录后查看你的历史生成</span>
                  <button type="button" className="GeneratePage-history-login" onClick={() => openLogin('/generate')}>登录查看</button>
                </div>
              )}

              {user?.token && isHistoryLoading && historyTasks.length === 0 && (
                <div className="GeneratePage-history-state" aria-live="polite">
                  <i className="bi bi-arrow-repeat GeneratePage-spin" aria-hidden="true" />
                  <span>正在读取历史记录</span>
                </div>
              )}

              {user?.token && historyError && historyTasks.length === 0 && (
                <div className="GeneratePage-history-state is-error" role="alert">
                  <span>{historyError}</span>
                  <button type="button" className="GeneratePage-history-retry" onClick={() => loadHistory(1, false)}>重试</button>
                </div>
              )}

              {user?.token && !isHistoryLoading && !historyError && historyTasks.length === 0 && (
                <div className="GeneratePage-history-state">
                  <i className="bi bi-clock-history" aria-hidden="true" />
                  <span>完成一次生成后，记录会出现在这里</span>
                </div>
              )}

              {historyTasks.length > 0 && (
                <div className="GeneratePage-history-list" role="list" aria-label="历史任务">
                  {historyTasks.map((task, index) => {
                    const preview = getTaskPreview(task);
                    const taskStatusMeta = getTaskStatusMeta(task.status);
                    const taskId = String(task.task_id || task.id || '');
                    const taskKey = taskId || `${task.created_at || 'task'}-${index}`;
                    const taskPrompt = getTaskPrompt(task);

                    return (
                      <article className="GeneratePage-history-item" key={taskKey} role="listitem">
                        <div className={`GeneratePage-history-thumb${preview ? ` is-${preview.type}` : ' is-empty'}`}>
                          {preview?.type === 'image' && <img src={preview.url} alt={`${getTaskModelName(task.app_name)}历史结果`} loading="lazy" />}
                          {preview?.type === 'video' && <video src={preview.url} muted playsInline preload="metadata" aria-label={`${getTaskModelName(task.app_name)}历史视频`} />}
                          {preview?.type === 'audio' && <i className="bi bi-music-note-beamed" aria-hidden="true" />}
                          {!preview && <i className={`bi ${task.status === 'failed' || task.status === 'webhook_error' ? 'bi-exclamation-triangle' : 'bi-stars'}`} aria-hidden="true" />}
                        </div>
                        <div className="GeneratePage-history-item-body">
                          <div className="GeneratePage-history-item-header">
                            <strong title={task.app_name}>{getTaskModelName(task.app_name)}</strong>
                            <span className={`GeneratePage-history-status ${taskStatusMeta.className}`}><i />{taskStatusMeta.label}</span>
                          </div>
                          <p title={taskPrompt || '暂无提示词'}>{taskPrompt || '该任务未记录提示词'}</p>
                          <div className="GeneratePage-history-item-footer">
                            <time dateTime={task.created_at || ''}>{formatTaskTime(task.created_at)}</time>
                            <span>{formatTaskPoints(task.money)} 积分</span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {user?.token && historyTasks.length > 0 && historyError && (
                <div className="GeneratePage-history-inline-error" role="alert">
                  <span>{historyError}</span>
                  <button type="button" className="GeneratePage-history-retry" onClick={() => loadHistory(historyPage + 1, true)}>重试</button>
                </div>
              )}

              {user?.token && historyTasks.length > 0 && historyHasMore && !historyError && (
                <button
                  type="button"
                  className="GeneratePage-history-load-more"
                  disabled={isHistoryLoadingMore}
                  onClick={() => loadHistory(historyPage + 1, true)}
                >
                  {isHistoryLoadingMore ? '正在加载…' : '加载更多'}
                  <i className="bi bi-arrow-down" aria-hidden="true" />
                </button>
              )}
            </section>
          </section>
        </div>
      </main>
    </div>
  );
}

export default GeneratePage;
